import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, BarChart3, UsersRound } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { UebersichtTab } from '@/components/admin/UebersichtTab';
import { AnalyticsTab } from '@/components/admin/AnalyticsTab';
import { AgentenVerwaltungTab } from '@/components/admin/AgentenVerwaltungTab';
import { AgentDashboard } from '@/components/dashboard/AgentDashboard';
import { formatDate } from '@/lib/formatLocale';

export const Route = createFileRoute('/_app/dashboard')({
  component: DashboardHome,
});

type Tab = 'uebersicht' | 'analytics' | 'agents';

function DashboardHome() {
  const { t, i18n } = useTranslation();
  const { profile } = useAuth();
  const [tab, setTab] = useState<Tab>('uebersicht');

  const isAdmin = profile?.role === 'admin';
  // Batch ZO #2: greeting now reads "Guten Tag, Herr Hodzic." instead of
  // "Guten Tag, Jasko." — formal Sie-Form per tester request.
  // If full_name already starts with Herr/Frau/Hr./Fr., keep it and grab
  // the last token as the surname. Otherwise prefix with the salutation
  // resolved from profile.title (Hr. → Herr, Fr. → Frau). Fall back to
  // full_name as-is when no salutation is available.
  const greetingName = (() => {
    const name = profile?.full_name?.trim() ?? '';
    if (!name) return '';
    const tokens = name.split(/\s+/);
    if (/^(Herr|Frau|Hr\.|Fr\.)$/i.test(tokens[0]!)) {
      return `${tokens[0]} ${tokens[tokens.length - 1]}`;
    }
    const last = tokens[tokens.length - 1] ?? name;
    const salutation =
      profile?.title === 'Hr.'
        ? 'Herr'
        : profile?.title === 'Fr.'
          ? 'Frau'
          : null;
    return salutation ? `${salutation} ${last}` : name;
  })();

  // Non-admin users see their personal AgentDashboard (MyDay + Next Actions)
  if (!isAdmin) {
    return (
      <section className="space-y-7">
        <header>
          <p className="text-[11px] uppercase text-ink-3 tracking-[0.12em]">
            {formatDate(new Date(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          <h2 className="mt-1 font-display text-3xl text-ink">
            {t('dashboard.greeting', 'Guten Tag')}
            {greetingName ? `, ${greetingName}` : ''}.
          </h2>
          <p className="mt-1 text-sm text-ink-3">
            {t('dashboard.agent_subtitle', 'Hier ist Ihr Tag im Überblick.')}
          </p>
        </header>
        <AgentDashboard />
      </section>
    );
  }

  return (
    <section className="space-y-6">
      <header>
        <p className="text-[11px] uppercase text-ink-3 tracking-[0.12em]">
          {formatDate(new Date(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
        <h2 className="mt-1 font-display text-3xl text-ink">
          {t('dashboard.greeting', 'Guten Tag')}
          {greetingName ? `, ${greetingName}` : ''}.
        </h2>
        <p className="mt-1 text-sm text-ink-3">
          {t('dashboard.admin_subtitle', 'Übersicht, Analytics und Agentenverwaltung — alles an einem Ort.')}
        </p>
      </header>

      <div className="border-b border-line">
        <nav className="-mb-px flex gap-1 overflow-x-auto scrollbar-thin">
          <TabButton
            active={tab === 'uebersicht'}
            onClick={() => setTab('uebersicht')}
            icon={<LayoutDashboard className="h-4 w-4" />}
            label={t('dashboard.tab.uebersicht', 'Übersicht')}
          />
          <TabButton
            active={tab === 'analytics'}
            onClick={() => setTab('analytics')}
            icon={<BarChart3 className="h-4 w-4" />}
            label={t('dashboard.tab.analytics', 'Analytics')}
          />
          <TabButton
            active={tab === 'agents'}
            onClick={() => setTab('agents')}
            icon={<UsersRound className="h-4 w-4" />}
            label={t('dashboard.tab.agents', 'Agenten')}
          />
        </nav>
      </div>

      {tab === 'uebersicht' && <UebersichtTab />}
      {tab === 'analytics' && <AnalyticsTab />}
      {tab === 'agents' && <AgentenVerwaltungTab />}
    </section>
  );
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors md:px-4 ${
        active
          ? 'border-ink text-ink'
          : 'border-transparent text-ink-3 hover:text-ink hover:border-line-strong'
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
